import { useEffect, useState } from "react";
import axios from "axios";
import { Package } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { PRODUCTS, formatCad, packPctFor } from "@/data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/account/household-bundle`;

export const HouseholdBundle = ({ t, refreshKey }) => {
  const { add } = useCart();
  const [bundle, setBundle] = useState(null);
  useEffect(() => {
    axios.get(API, { withCredentials: true }).then((r) => setBundle(r.data)).catch(() => setBundle(null));
  }, [refreshKey]);

  const items = (Array.isArray(bundle?.items) ? bundle.items : [])
    .map((i) => ({ ...i, product: PRODUCTS.find((p) => p.id === i.product_id) })).filter((i) => i.product);
  const jars = items.reduce((n, i) => n + i.qty, 0);
  if (jars < 2) return null;
  const subtotal = items.reduce((s, i) => s + i.qty * i.product.priceCents, 0);
  const pct = packPctFor(jars);
  const saving = Math.floor((subtotal * pct) / 100);
  const addAll = () => items.forEach((i) => { for (let n = 0; n < i.qty; n += 1) add(i.product_id); });

  return (
    <div className="mt-8 border border-[#B5A642]/40 rounded-sm p-6 bg-[#B5A642]/[0.04]" data-testid="household-bundle">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#B5A642]">
        <Package className="w-3.5 h-3.5" /> {t("The household bundle", "L'ensemble de la maisonnée")}
      </div>
      <p className="mt-3 font-display text-2xl text-[#E8DDC7]">{t(`Everything the house needs for about ${bundle.days} days`, `Tout ce qu'il faut à la maison pour environ ${bundle.days} jours`)}</p>
      <ul className="mt-4 space-y-2">
        {items.map((i) => (
          <li key={i.product_id} className="flex flex-wrap justify-between gap-2 text-[14px] text-[#D3CEC4]/80" data-testid={`bundle-item-${i.product_id}`}>
            <span>{i.qty} × {i.product.name} <span className="italic text-[#D3CEC4]/55">· {t("for", "pour")} {i.dogs.join(" & ")}</span></span>
            <span className="font-ledger">{formatCad(i.qty * i.product.priceCents)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-3 border-t border-[#303B2D]/60 flex flex-wrap items-center justify-between gap-4">
        <p className="font-ledger text-[13px] text-[#E8DDC7]" data-testid="bundle-total">
          {pct > 0 && <span className="text-[#8FB08A]">{t(`Multi-jar savings −${pct}%`, `Rabais multi-pots −${pct} %`)} ({formatCad(saving)}) · </span>}
          {formatCad(subtotal - saving)}
        </p>
        <button onClick={addAll} data-testid="bundle-add-all"
          className="px-6 py-3 bg-[#4B3226] text-[#E8DDC7] text-[11px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 transition-[filter] duration-300">
          {t("Add the bundle to the basket", "Ajouter l'ensemble au panier")}
        </button>
      </div>
      <p className="mt-3 text-[12px] text-[#D3CEC4]/55">{t("Quantities follow each dog's daily measure. Pack savings apply in the basket.", "Les quantités suivent la mesure quotidienne de chaque chien. Les économies s'appliquent au panier.")}</p>
    </div>
  );
};
